import * as THREE from "three"
import type { CameraLivePose } from "@/lib/location-scene-3d/camera-live-sync"

export type CameraLivePoseSmootherOptions = {
  onApply: (pose: CameraLivePose) => void
  /** Damp lambda (higher = snappier). Default 14. */
  lambda?: number
}

export type CameraLivePoseSmoother = {
  setTarget: (pose: CameraLivePose) => void
  /** Drop current/target so the next setTarget snaps. */
  reset: () => void
  dispose: () => void
}

const DEFAULT_LAMBDA = 14
const POS_EPS = 0.0004
const ANGLE_EPS = 0.0008
const FOV_EPS = 0.05

function clonePose(pose: CameraLivePose): CameraLivePose {
  return {
    pos: [pose.pos[0], pose.pos[1], pose.pos[2]],
    yaw: pose.yaw,
    pitch: pose.pitch,
    fov: pose.fov,
  }
}

function shortestAngleDelta(from: number, to: number): number {
  let d = to - from
  while (d > Math.PI) d -= Math.PI * 2
  while (d < -Math.PI) d += Math.PI * 2
  return d
}

function dampAngle(current: number, target: number, lambda: number, dt: number): number {
  const delta = shortestAngleDelta(current, target)
  return current + delta * (1 - Math.exp(-lambda * dt))
}

function poseNear(a: CameraLivePose, b: CameraLivePose): boolean {
  const dx = a.pos[0] - b.pos[0]
  const dy = a.pos[1] - b.pos[1]
  const dz = a.pos[2] - b.pos[2]
  if (dx * dx + dy * dy + dz * dz > POS_EPS) return false
  if (Math.abs(shortestAngleDelta(a.yaw, b.yaw)) > ANGLE_EPS) return false
  if (Math.abs(a.pitch - b.pitch) > ANGLE_EPS) return false
  if (Math.abs(a.fov - b.fov) > FOV_EPS) return false
  return true
}

/**
 * Frame-rate independent damp from last applied pose toward remote live targets.
 * First target snaps; later packets only update the goal.
 */
export function createCameraLivePoseSmoother(options: CameraLivePoseSmootherOptions): CameraLivePoseSmoother {
  const { onApply, lambda = DEFAULT_LAMBDA } = options

  let cancelled = false
  let target: CameraLivePose | null = null
  let current: CameraLivePose | null = null
  let rafId = 0
  let lastTs = 0

  function stopLoop() {
    if (rafId !== 0) {
      cancelAnimationFrame(rafId)
      rafId = 0
    }
    lastTs = 0
  }

  function tick(now: number) {
    rafId = 0
    if (cancelled || !current || !target) return

    const dt = lastTs > 0 ? Math.min(0.05, Math.max(0, (now - lastTs) / 1000)) : 1 / 60
    lastTs = now

    current = {
      pos: [
        THREE.MathUtils.damp(current.pos[0], target.pos[0], lambda, dt),
        THREE.MathUtils.damp(current.pos[1], target.pos[1], lambda, dt),
        THREE.MathUtils.damp(current.pos[2], target.pos[2], lambda, dt),
      ],
      yaw: dampAngle(current.yaw, target.yaw, lambda, dt),
      pitch: THREE.MathUtils.damp(current.pitch, target.pitch, lambda, dt),
      fov: THREE.MathUtils.damp(current.fov, target.fov, lambda, dt),
    }

    onApply(current)

    if (poseNear(current, target)) {
      current = clonePose(target)
      onApply(current)
      lastTs = 0
      return
    }

    rafId = requestAnimationFrame(tick)
  }

  function ensureLoop() {
    if (cancelled || rafId !== 0) return
    rafId = requestAnimationFrame(tick)
  }

  function setTarget(pose: CameraLivePose) {
    if (cancelled) return
    target = clonePose(pose)
    if (!current) {
      current = clonePose(pose)
      onApply(current)
      return
    }
    ensureLoop()
  }

  function reset() {
    stopLoop()
    target = null
    current = null
  }

  function dispose() {
    cancelled = true
    stopLoop()
    target = null
    current = null
  }

  return { setTarget, reset, dispose }
}
